import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

type UseInfiniteScrollOptions = {
	enabled: boolean;
	hasNextPage: boolean;
	isFetchingNextPage: boolean;
	fetchNextPage: () => void;
	/**
	 * Opaque data reference whose change triggers scroll-position restoration.
	 * When the consumer's grid data updates, pass the new array (or any value
	 * that changes identity) to keep the scroll pinned.
	 */
	data?: unknown;
	/**
	 * Optional CSS selector used to find the scroll container if the consumer
	 * hasn't dispatched a scroll event yet (so the ref is still null).
	 */
	fallbackContainerSelector?: string;
	/**
	 * Pixel tolerance for "near bottom" detection. At browser zoom levels
	 * other than 100%, `scrollTop`, `clientHeight`, and `scrollHeight` can
	 * disagree by ~1px due to sub-pixel rounding, so a strict equality check
	 * against `scrollHeight` will intermittently miss. Defaults to 2px.
	 */
	threshold?: number;
};

/**
 * Wires up infinite-scroll fetching plus scroll-position bookkeeping for a
 * Kendo Grid in scrollable mode. Returns a `handleScroll` to wire to the
 * grid's `onScroll` and a `resetScrollPosition` imperative for callers that
 * want to clear the saved position on filter/sort changes.
 *
 * Restoration behavior: while `isFetchingNextPage` is true the container is
 * pinned to the bottom (so newly-appended skeleton rows are visible); once the
 * fetch completes the container is restored to the last captured `scrollTop`.
 */
export const useInfiniteScroll = ({
	enabled,
	hasNextPage,
	isFetchingNextPage,
	fetchNextPage,
	data,
	fallbackContainerSelector,
	threshold = 2,
}: UseInfiniteScrollOptions) => {
	const fetchingRef = useRef(false);
	const lastScrollTopRef = useRef(0);
	const scrollContainerRef = useRef<HTMLElement | null>(null);

	const handleScroll = useCallback(
		(event: { nativeEvent: Event }) => {
			const target = event.nativeEvent.target as HTMLElement;
			scrollContainerRef.current = target;
			lastScrollTopRef.current = target.scrollTop;
			if (!enabled || !hasNextPage || isFetchingNextPage || fetchingRef.current)
				return;
			// Use a small pixel tolerance instead of strict `>=` because at
			// non-100% browser zoom levels, scrollTop/clientHeight/scrollHeight
			// round at sub-pixel boundaries and the exact equality can miss.
			const distanceFromBottom =
				target.scrollHeight - (target.scrollTop + target.clientHeight);
			if (distanceFromBottom <= threshold) {
				fetchingRef.current = true;
				fetchNextPage();
			}
		},
		[enabled, hasNextPage, isFetchingNextPage, fetchNextPage, threshold],
	);

	// Reset the fetch guard after fetch completes
	useEffect(() => {
		if (!isFetchingNextPage && fetchingRef.current) {
			fetchingRef.current = false;
		}
	}, [isFetchingNextPage]);

	// Restore scroll position after data updates. While fetching the next page
	// the container is pinned to the bottom; otherwise it returns to the last
	// captured scrollTop.
	useLayoutEffect(() => {
		if (!enabled) return;
		if (lastScrollTopRef.current <= 0) return;
		const container =
			scrollContainerRef.current ??
			(fallbackContainerSelector
				? document.querySelector<HTMLElement>(fallbackContainerSelector)
				: null);
		if (!container) return;
		const targetScrollTop = isFetchingNextPage
			? container.scrollHeight - container.clientHeight
			: lastScrollTopRef.current;
		if (container.scrollTop !== targetScrollTop) {
			container.scrollTop = targetScrollTop;
		}
	}, [enabled, data, isFetchingNextPage, fallbackContainerSelector]);

	const resetScrollPosition = useCallback(() => {
		lastScrollTopRef.current = 0;
	}, []);

	return { handleScroll, resetScrollPosition };
};
